import _ from 'lodash'

function sortData ({ chart, chartData, preparedData, forChart }) {
	sortSeries({ chart, chartData, preparedData, forChart })
	sortRanges({ chart, preparedData })
	sortPlots({ chart, preparedData })
}

function sortSeries ({ chart, chartData, preparedData, forChart }) {
	preparedData.plots.forEach(plot => {

		// Group by axis (chart goes right to left)
		let rightSorted = getSortedAxis({ chart, plot, axis: 'right' })
		let leftSorted = getSortedAxis({ chart, plot, axis: 'left' })

		// #43104 the series need to be sorted in reverse order for some plot types
		// so that they render correctly, but don't do that if we're preparing
		// data for the data table (ie !forChart)
		if (!_.includes(NON_REVERSIBLE, chart.get('plotType')) && forChart) {
			_.reverse(rightSorted)
			_.reverse(leftSorted)
		}

		// #48491 #52506 even though chart wants right-then-left, we want
		// left-then-right for tables/legends
		let fullySorted = forChart
			? rightSorted.concat(leftSorted)
			: leftSorted.concat(rightSorted)

		plot.series = fullySorted
	})
}

const NON_REVERSIBLE = ['pie', 'donut', 'cycle', 'process', 'timeline', 'funnel']

function getSortedAxis ({ chart, plot, axis }) {
	let axisSeries = plot.series.filter(serie => serie.axis === axis)

	// Always put in manual order first; if bars are set to non-manual, they'll
	// be adjusted later
	axisSeries = sort({
		chart,
		sortType: 'manual',
		series: axisSeries })

	let sortSettings = chart.get(`${axis}AxisBarSortSettings`)
	let sortType = _.get(sortSettings, 'sortType')
	if (!sortType || sortType === 'manual') {
		// We already sorted the entire axis manually, so if the bars are sorted
		// manually, then we're done
		return axisSeries
	}

	// https://github.com/SpiderStrategies/Scoreboard/issues/36714#issuecomment-1045225349
	// When doing non-manual sorting for bars:
	//  1. All bar series are grouped together
	//  2. All other features are ordered manually
	//
	// Everything was ordered manually already, so we can pull out any
	// non-bar series before and after the first bar serie, sort the bars
	// by value or label, then put them all back together
	let firstBarIndex = axisSeries.findIndex(s => s.type === 'bar')
	let beforeBars = firstBarIndex == -1
		? axisSeries
		: axisSeries.slice(0, firstBarIndex)
	let afterBars = firstBarIndex === -1 || (firstBarIndex + 1) >= axisSeries.length
		? []
		: axisSeries.slice(firstBarIndex + 1).filter(s => s.type !== 'bar')

	let barSeries = axisSeries.filter(s => s.type === 'bar')

	let sortedBars = sort({
		chart,
		sortType,
		direction: sortSettings.direction,
		series: barSeries })

	if (sortSettings.direction === 'descending') {
		_.reverse(sortedBars)
	}

	return beforeBars.concat(sortedBars).concat(afterBars)
}

function sort ({chart, series, sortType, direction }) {
	return series.map(s => {
		// In the database, "top level" features have a sort order,
		// then child features have their own sort order within their parent
		// feature. So it could look like this:
		//
		// 0: line serie A
		// 1: bar stack
		//    0: bar serie A
		//    1: bar serie B
		// 2: line serie B
		//
		// But in the ChartData's plots.series, the parent features don't exist,
		// and the child series need to go in the parent's slot. So the above
		// would be ordered like this in ChartData.plots.series
		//
		//   line serie A
		//   bar serie A
		//   bar serie B
		//   line serie B

		// So we need to sort by parent sort order, then child sort order.
		// Figure out all those pairs ahead of time
		let underlyingSerie = chart.findFeatureByUnderlyingSerieId(s)
		let stack
		if (!_.isNil(s.stackBy)) {
			stack = chart.features.find(p => p.children.includes(underlyingSerie))
		}

		let sortPair = [ firstSortValue({
			chart,
			dataSerie: s,
			underlyingSerie,
			stack,
			sortType
		})]

		// For bars in a stack, we need to add in the manual sortOrder within
		// the stack as a secondary sort value
		let orderWithinStack = stack ? underlyingSerie.get('sortOrder') : 0
		if (direction === 'descending') {
			// If the bars are descending order, we'll be reversing them later.
			// BUT we want the manual order within a stack to be honored, so
			// if we're going to reverse later, need to reverse the bars within
			// a stack now to make sure they are in the correct order later
			orderWithinStack *= -1
		}
		sortPair.push(orderWithinStack)

		return { serie: s, sortValues: sortPair }
	}).sort((o1, o2) => {
		return compare(o1.sortValues, o2.sortValues)
	}).map(o => o.serie)
}

function firstSortValue ({ chart, dataSerie, underlyingSerie, stack, sortType }) {
	if (sortType === 'manual') {
		if (_.isNil(stack)) {
			// For regular bars, use their explicit sortOrder
			return underlyingSerie.get('sortOrder')
		} else {
			// For bars in a stack, use the stack's sortOrder
			return stack.get('sortOrder')
		}
	} else if (sortType === 'by-label') {
		if (_.isNil(stack)) {
			// For regular bars, just sort by label
			return dataSerie.title.toLowerCase()
		} else {
			// For bars in a stack, sort by the stack's label
			return stack.getLabel({ data: chart.data }).toLowerCase()
		}
	} else if (sortType === 'by-value') {
		// Even if using stacks, the sortValue is already aggregated for the
		// entire stack, so we can use that directly
		return dataSerie.sortValue
	} else {
		throw Error("invalid sortBy: " + sortType)
	}
}

function compare (values1, values2) {
	let v1 = values1[0]
	let v2 = values2[0]

	if (v1 < v2) {
		return -1
	} else if (v1 > v2) {
		return 1
	} else if (values1.length > 1 && values2.length > 1) {
		return compare(values1.slice(1), values2.slice(1))
	} else {
		return 0
	}
}

function sortRanges ({ chart, preparedData }) {
	preparedData.plots.forEach(plot => {
		let sortedRanges = sortPlotsOrRanges({
			items: plot.breakdown.range,
			chart,
			rangeIdsGetter: range => {
				return {
					id: range.underlyingRangeId,
					tempId: range.underlyingRangeTempId
				}
			},
			labelGetter: range => range.title
		})

		plot.breakdown.range = sortedRanges

		let sortIndexByRangeId = _(sortedRanges)
			.map((range, index) => {
				return [range.id, index]
			})
			.fromPairs()
			.value()

		// series.points needs to be in the same order as breakdown.range
		plot.series.forEach(serie => {
			serie.points = _.sortBy(serie.points, point => {
				return sortIndexByRangeId[point.rangeId]
			})
		})
	})
}

function sortPlots ({ chart, preparedData }) {
	preparedData.plots = sortPlotsOrRanges({
		items: preparedData.plots,
		chart,
		rangeIdsGetter: plot => {
			return {
				id: plot.breakdown.underlyingRangeId,
				tempId: plot.breakdown.underlyingRangeTempId
			}
		},
		labelGetter: plot => plot.breakdown.title
	})
}

function sortPlotsOrRanges ({ items, rangeIdsGetter, labelGetter, chart }) {
	if (_.isEmpty(items)) return items

	const item = items[0]
	const breakdown = chart.breakdowns.find(b =>
		!_.isNil(b.ranges.findByIds(rangeIdsGetter(item))))
	if (_.isNil(breakdown)) return items

	let sortSettings = breakdown.get('sortSettings') || { sortType: 'manual'}
	let sortType = sortSettings.sortType

	let sorted = _.sortBy(items, item => {
		if (sortType === 'manual') {
			let underlyingRange = breakdown.ranges.findByIds(rangeIdsGetter(item))

			return underlyingRange.get('sortOrder')
		} else if (sortType === 'by-value') {
			return item.sortValue
		} else if (sortType === 'by-label') {
			return labelGetter(item).toLowerCase()
		}
	})

	if (sortType !== 'manual' && sortSettings.direction === 'descending') {
		_.reverse(sorted)
	}

	return sorted
}

export default sortData

sortData.__filename__ = 'charts/chart-data/sort-data.js'
